import type { FC } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import type { UseTranslationResponse } from 'react-i18next';
import type { DataType } from '@/pages/+data';
import type { UserRecent } from '@/types/domain/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Flex, FormItem, Icon, Modal, Section, Select, TextArea, Tooltip, Typography } from '@ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { z } from 'zod';
import { feedbackPost } from '@/api/core/feedback/feedbackPost';
import { GenericPageSectionWrapper } from '@/components/GenericPageSectionWrapper/GenericPageSectionWrapper';
import { useUserRecentFlights } from '@/queries/useUserRecentFlights';
import { getAuthUrlWithRedirect } from '@/utils/getAuthUrlWithRedirect';
import { getLinkHref } from '@/utils/getLinkHref';
import styles from './FeedbackSection.module.css';

type CallsignSelectProps = {
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
};

const CallsignSelect: FC<CallsignSelectProps> = ({ value, onChange, disabled }) => {
    const { t } = useTranslation();

    const { data } = useUserRecentFlights();
    const options = [
        { label: t('Contacts_Form_Callsign_Empty'), value: ' ' },
        ...data.map(flight => ({ label: flight.callsign, value: flight.id.toString() }))
    ];
    return (
        <Select
            placeholder={t('Contacts_Form_Callsign')}
            options={options}
            value={value}
            onChange={onChange}
            disabled={disabled}
        />
    );
};

const MIN_LENGTH = 12;
const MAX_LENGTH = 2000;

const formSchema = (t: UseTranslationResponse<'translation', undefined>['t']) => z.object({
    callsign: z.string().optional(),
    content: z.string()
        .min(MIN_LENGTH, t('Contacts_Form_Error_Min').replace('{{value}}', MIN_LENGTH.toString()))
        .max(MAX_LENGTH, t('Contacts_Form_Error_Max').replace('{{value}}', MAX_LENGTH.toString())),
    anon: z.boolean()
});

type FormFields = z.infer<ReturnType<typeof formSchema>>;

export const FeedbackSection: FC = () => {
    const pageContext = usePageContext();
    const { user, isClientSide } = pageContext;
    const queryClient = useQueryClient();
    const { publicEnableFeedback } = useData<DataType>();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const { t } = useTranslation();

    const { control, reset, handleSubmit, getValues, formState: { isSubmitting }, watch } = useForm<FormFields>({
        defaultValues: {
            content: isClientSide ? sessionStorage.getItem('content') || '' : '',
            callsign: '',
            anon: false
        },
        resolver: zodResolver(formSchema(t))
    });

    const contentStorage = {
        save: () => isClientSide && getValues('content') && sessionStorage.setItem('content', getValues('content')),
        remove: () => isClientSide && sessionStorage.removeItem('content')
    };

    const onSubmit: SubmitHandler<FormFields> = (data) => {
        if (!user)
            return setIsModalOpen(true);

        const callsigns = queryClient.getQueryData<UserRecent[]>(['userRecentFlights']);
        const callsign = !data.anon && data.callsign?.trim()
            ? callsigns?.find(c => c.id === Number(data.callsign))?.callsign
            : undefined;

        const postFn = feedbackPost({ ...data, callsign })
            .then(() => {
                contentStorage.remove();
                reset();
            })
            .catch((e) => {
                console.error(e);
                throw e;
            });

        toast.promise(postFn, {
            success: t('Feedback_Send_Success'),
            error: t('Feedback_Send_Error'),
            loading: t('Feedback_Send_Loading')
        });
    };

    return (
        <>
            <GenericPageSectionWrapper size="l">
                <Flex gap={80}>
                    <Flex flex="0 0 480px" vertical gap={80}>
                        <Typography.Heading color="textSecondary">{t('Contacts_Title')}</Typography.Heading>
                        <Typography.Paragraph color="textMain">
                            {t('Contacts_Description_Paragraph1')}
                            {' '}
                            <a
                                className={styles.link}
                                href="https://discord.vatsim-petersburg.com"
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                {t('Contacts_Description_Paragraph1_Link')}
                            </a>
                            <br />
                            <br />
                            {t('Contacts_Description_Paragraph2')}
                        </Typography.Paragraph>
                    </Flex>
                    <Section block>
                        {/* eslint-disable-next-line ts/no-misused-promises */}
                        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                            <Typography.Paragraph color="textPrimary">{t('Contacts_Form_Title')}</Typography.Paragraph>
                            {user && (
                                <Flex justify="space-between" align="center" style={{ paddingTop: 8 }}>
                                    <Typography.Paragraph color="textAccent">{`${user.first_name} ${user.last_name}`}</Typography.Paragraph>
                                    <FormItem
                                        name="callsign"
                                        control={control}
                                        flex="0 0"
                                        render={({ field }) => {
                                            const select = (
                                                <CallsignSelect
                                                    {...field}
                                                    disabled={watch('anon')}
                                                    value={field.value?.trim()}
                                                />
                                            );
                                            return watch('anon')
                                                ? <Tooltip title={t('Contacts_Form_Callsign_Disabled')}>{select}</Tooltip>
                                                : select;
                                        }}
                                    />
                                </Flex>
                            )}
                            <FormItem
                                name="content"
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        {...field}
                                        placeholder={t('Contacts_Form_Placeholder')}
                                        block
                                    />
                                )}
                            />
                            {user && (
                                <FormItem
                                    name="anon"
                                    control={control}
                                    render={({ field: { value, onChange, ...props } }) => (
                                        <Checkbox
                                            {...props}
                                            checked={value}
                                            onCheckedChange={onChange}
                                            label={(
                                                <Flex gap={4} align="center" style={{ paddingBlock: 8 }}>
                                                    <Typography.Paragraph level={2} color="textMain">
                                                        {t('Contacts_Form_Anon')}
                                                    </Typography.Paragraph>
                                                    <a href="/privacy" className={styles.linkToPrivacy}>
                                                        {t('Contacts_Form_Anon_More')}
                                                    </a>
                                                </Flex>
                                            )}
                                        />
                                    )}
                                />
                            )}
                            <Flex justify="space-between" align="center">
                                <Button type="submit" disabled={isSubmitting || !publicEnableFeedback} icon={!publicEnableFeedback ? <Icon.Construction /> : undefined}>
                                    {t(publicEnableFeedback ? 'Contacts_Form_Submit' : 'Contacts_Form_Submit_Disabled')}
                                </Button>
                                {!user && (
                                    <a
                                        href={getAuthUrlWithRedirect(getLinkHref(pageContext, '/#contacts'))}
                                        onClick={contentStorage.save}
                                        className={styles.login}
                                    >
                                        {t('Contacts_Form_Login')}
                                    </a>
                                )}
                            </Flex>
                        </form>
                    </Section>
                </Flex>
            </GenericPageSectionWrapper>
            {!user && (
                <Modal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    showCloseButton={false}
                    aria-title={t('Contacts_Modal_Title')}
                    aria-description={t('Contacts_Modal_Description')}
                >
                    <Flex vertical align="center" gap={16} className={styles.modal}>
                        <Icon.Login size={64} color="iconMain" />
                        <Flex vertical gap={10}>
                            <Modal.Title>{t('Contacts_Modal_Title')}</Modal.Title>
                            <Modal.Description>{t('Contacts_Modal_Description')}</Modal.Description>
                        </Flex>
                        <Flex gap={8} className={styles.modalButtons}>
                            <Flex>
                                <Button
                                    variant="secondary"
                                    block
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    {t('Contacts_Modal_Refuse')}
                                </Button>
                            </Flex>
                            <Button
                                href={getAuthUrlWithRedirect(getLinkHref(pageContext, '/#contacts'))}
                                onClick={contentStorage.save}
                            >
                                {t('Contacts_Modal_Accept')}
                            </Button>
                        </Flex>
                    </Flex>
                </Modal>
            )}
        </>
    );
};
